/*
 * ⚠️ DEVELOPMENT & TESTING COMPONENT ⚠️
 * 
 * This component is used strictly for testing the Google Maps Geocoding API integration.
 * It serves as a proof-of-concept for auto-filling address details based on a Zip Code.
 * 
 * Intended Use:
 * - Verifying the API Key configuration in the .env file.
 * - Testing the responsiveness and accuracy of the Geocoding API.
 * - Debugging address parsing logic (City, State, Country).
 * 
 * DO NOT USE IN PRODUCTION ROUTES.
 */

import React, { useState } from "react";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_BOOT_API_KEY;

export const LocationTest = () => {
  const [zip, setZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [city, setCity] = useState("");
  const [stateRegion, setStateRegion] = useState("");
  const [country, setCountry] = useState("");
  const [raw, setRaw] = useState(null);

  /**
   * Resets the state for a fresh search.
   */
  const resetResult = () => {
    setCity("");
    setStateRegion("");
    setCountry("");
    setRaw(null);
    setError("");
  };

  /**
   * Fetches location data from Google Geocoding API.
   * Triggered automatically when 5 digits are entered.
   */
  const lookupZip = async (value) => {
    if (!GOOGLE_API_KEY) {
      setError(
        "Error: Missing REACT_APP_GOOGLE_PLACES_BOOT_API_KEY environment variable.\n" +
        "Please check your .env file and restart the development server."
      );
      return;
    }

    try {
      setLoading(true);
      setError("");
      // Reset previous results
      setCity("");
      setStateRegion("");
      setCountry("");
      setRaw(null);

      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
        value.trim()
      )}&key=${GOOGLE_API_KEY}`;

      const res = await fetch(url);
      const data = await res.json();
      setRaw(data);

      if (data.status !== "OK" || !data.results || data.results.length === 0) {
        setError(`No results found. API Status: ${data.status}`);
        return;
      }

      // Parse Address Components
      const components = data.results[0].address_components || [];

      // Logic to find City (locality or administrative_area_level_2 for some regions)
      const cityComp =
        components.find((c) => c.types.includes("locality")) ||
        components.find((c) =>
          c.types.includes("administrative_area_level_2")
        );

      // Logic to find State/Region
      const stateComp = components.find((c) =>
        c.types.includes("administrative_area_level_1")
      );

      // Logic to find Country
      const countryComp = components.find((c) =>
        c.types.includes("country")
      );

      setCity(cityComp?.long_name || "");
      setStateRegion(stateComp?.long_name || "");
      setCountry(countryComp?.long_name || "");

    } catch (err) {
      console.error("Geocoding API Error:", err);
      setError("An error occurred while calling the Geocoding API.");
    } finally {
      setLoading(false);
    }
  };

  const handleZipChange = (e) => {
    const value = e.target.value;

    // Allow only numbers
    if (!/^\d*$/.test(value)) return;

    setZip(value);
    resetResult();

    // Trigger lookup when 5 digits are entered
    if (value.length === 5 && /^\d{5}$/.test(value)) {
      lookupZip(value);
    }
  };

  const hasKey = Boolean(GOOGLE_API_KEY);

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "2rem",
        fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        background: "#f0f2f5",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start"
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "700px",
          background: "#ffffff",
          borderRadius: "12px",
          padding: "2rem",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        {/* Environment Status Banner */}
        <div
          style={{
            marginBottom: "1.5rem",
            padding: "0.75rem 1rem",
            borderRadius: "6px",
            fontSize: "0.875rem",
            fontWeight: "500",
            background: hasKey ? "#ecfdf5" : "#fef2f2",
            color: hasKey ? "#047857" : "#b91c1c",
            border: `1px solid ${hasKey ? "#a7f3d0" : "#fecaca"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <span>API Key Status</span>
          <span>
            {hasKey
              ? "✅ Loaded Successfully"
              : "❌ Not Found (Check .env)"}
          </span>
        </div>

        <div style={{ borderBottom: "1px solid #e5e7eb", paddingBottom: "1rem", marginBottom: "1.5rem" }}>
          <h1 style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#111827", margin: "0 0 0.5rem 0" }}>
            📍 Location Test (Auto-Lookup)
          </h1>
          <p style={{ color: "#6b7280", margin: 0, fontSize: "0.95rem" }}>
            Enter a ZIP code to test the geocoding integration. The lookup triggers automatically upon entering <strong>5 digits</strong>.
          </p>
        </div>

        {/* Input Section */}
        <div
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: "0.875rem", fontWeight: "500", color: "#374151", marginBottom: "0.5rem" }}>
              Zip Code
            </label>
            <input
              type="text"
              value={zip}
              onChange={handleZipChange}
              placeholder="e.g. 10001"
              maxLength={5}
              style={{
                width: "100%",
                padding: "0.75rem",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "1rem",
                outline: "none",
                transition: "border-color 0.15s ease-in-out",
              }}
            />
          </div>

          {loading && (
            <div style={{ paddingTop: "1.7rem", color: "#2563eb", fontSize: "0.9rem", fontWeight: "500" }}>
              Searching...
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: "1rem",
              background: "#fef2f2",
              color: "#b91c1c",
              borderRadius: "6px",
              fontSize: "0.9rem",
              marginBottom: "1.5rem",
              border: "1px solid #fecaca"
            }}
          >
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Results Section */}
        {(city || stateRegion || country) && (
          <div
            style={{
              padding: "1.5rem",
              background: "#eff6ff",
              borderRadius: "8px",
              border: "1px solid #dbeafe",
              marginBottom: "1.5rem"
            }}
          >
            <h3 style={{ margin: "0 0 1rem 0", fontSize: "1.1rem", color: "#1e40af" }}>
              Parsed Result
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "0.5rem 1.5rem", alignItems: "center" }}>
              <span style={{ fontWeight: "600", color: "#374151" }}>City:</span>
              <span>{city || "—"}</span>

              <span style={{ fontWeight: "600", color: "#374151" }}>State / Region:</span>
              <span>{stateRegion || "—"}</span>

              <span style={{ fontWeight: "600", color: "#374151" }}>Country:</span>
              <span>{country || "—"}</span>
            </div>
          </div>
        )}

        {/* Raw JSON Response */}
        {raw && (
          <details style={{ background: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
            <summary style={{
              cursor: "pointer",
              padding: "0.75rem 1rem",
              fontSize: "0.875rem",
              fontWeight: "500",
              color: "#4b5563"
            }}>
              View Raw JSON Response
            </summary>
            <div style={{ padding: "0 1rem 1rem 1rem" }}>
              <pre
                style={{
                  maxHeight: "300px",
                  overflow: "auto",
                  background: "#1f2937",
                  color: "#e5e7eb",
                  padding: "1rem",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  fontFamily: "Menlo, Monaco, Consolas, 'Courier New', monospace"
                }}
              >
                {JSON.stringify(raw, null, 2)}
              </pre>
            </div>
          </details>
        )}

        {/* Hint */}
        {!raw && !error && !loading && !city && (
          <p
            style={{
              marginTop: "2rem",
              textAlign: "center",
              fontSize: "0.875rem",
              color: "#6b7280",
            }}
          >
            Try entering <b>10001</b> to see New York / United States populate automatically.
          </p>
        )}
      </div>
    </div>
  );
};

